import { Formik, FormikProps } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalProps,
  Platform,
  ScrollView,
  useWindowDimensions
} from 'react-native';
import styled from 'styled-components/native';
import * as Yup from 'yup';

import { Feather } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';

import ContentView from '../../../components/ContentView';
import Form from '../../../components/form/Form';
import FormButton from '../../../components/form/FormButton';
import FormInput from '../../../components/form/FormInput';
import IconButton from '../../../components/IconButton';
import Link from '../../../components/Link';
import Spacer from '../../../components/Spacer';
import { H1, P, Subtitle } from '../../../components/Typography';
import Colors from '../../../constants/Colors';
import { StackNavigationProp } from '@react-navigation/stack';
import useLogin from '../../../hooks/user/useLogin';
import ForgotPasswordModal from './ForgotPasswordModal';

const StyledDimContainer = styled.View`
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
`;

const StyledDialog = styled.KeyboardAvoidingView<{ isMobile: boolean }>`
  width: ${p => (p.isMobile ? 100 : 90)}%;
  height: ${p => (p.isMobile ? 100 : 90)}%;
  border-radius: 10px;
  max-width: 1080px;
  max-height: ${p => (p.isMobile ? '100%' : '627px')};
  overflow: hidden;
  background-color: ${Colors.background};
`;

const StyledImageBackground = styled.ImageBackground<{ isDesktop: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${p => (p.isDesktop ? '100%' : '130%')};
  height: 100%;
  max-height: 100%;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: ${Platform.OS === 'ios' ? 48 : 20}px;
  right: 20px;
  z-index: 1;
`;

const StyledContainerView = styled.View<{ isDesktop: boolean }>`
  flex-direction: ${p => (p.isDesktop ? 'row' : 'column')};
`;

const StyledContainerColumn = styled.View<{ isDesktop: boolean }>`
  flex: ${p => (p.isDesktop ? 1 : '0 1 auto')};
  align-items: center;
`;

const StyledHeaderView = styled.View<{ isDesktop: boolean }>`
  padding: ${p =>
    p.isDesktop
      ? '124px 0 0 0'
      : `${Platform.OS === 'ios' ? 64 : 37}px 24px 0 24px`};
  max-width: ${p => (p.isDesktop ? 320 : 430)}px;
  width: 100%;
`;

const StyledH1 = styled(H1)`
  max-width: 320px;
`;

const StyledFormView = styled.View<{ isDesktop: boolean }>`
  padding-top: ${p => (p.isDesktop ? 168 : 0)}px;
  max-width: 430px;
  width: 100%;
`;

const StyledP = styled(P)`
  text-align: center;
  color: ${Colors.errorCaption};
`;

export type LogInModalProps = ModalProps & {
  onRequestSignUp?: () => void;
};

const LogInModal: React.FC<LogInModalProps> = ({
  onRequestSignUp,
  ...props
}) => {
  const { onRequestClose } = props;
  const window = useWindowDimensions();
  const isDesktop = window.width >= 768;
  const isMobile = window.width < 468;
  const formikRef = useRef<FormikProps<any> | null>();
  const [forgotPassword, setForgotPassword] = useState<boolean>();
  const navigation =
    useNavigation<
      StackNavigationProp<{ DrawerNavigator: any; Welcome: undefined }>
    >();
  const { jwtToken, loadingUser, loadingError, login } = useLogin();
  // enable/disable form button loading spinner
  useEffect(() => {
    formikRef.current?.setSubmitting(loadingUser);
  }, [loadingUser]);
  // after logging in navigate to circle invite or select a circle
  useEffect(() => {
    if (jwtToken?.user) {
      navigation.replace('DrawerNavigator', {
        screen: 'BottomTabNav',
        params: { screen: 'StudiesNav' }
      });
    }
  }, [navigation, jwtToken?.user]);

  return (
    <Modal animationType='fade' transparent {...props}>
      <StyledDimContainer>
        <StyledDialog
          isMobile={isMobile}
          behavior={Platform.select({ ios: 'padding' })}
        >
          <ScrollView
            scrollIndicatorInsets={{ right: 1 }}
            contentContainerStyle={{
              flexGrow: 1 // Fill the dialog
            }}
          >
            <StyledImageBackground
              isDesktop={isDesktop}
              resizeMode='stretch'
              source={require('../../../assets/sign-up/abstract-background.png')}
            />
            <StyledIconButton onPress={event => onRequestClose?.(event)}>
              <Feather name='x' size={28} color='white' />
            </StyledIconButton>

            <StyledContainerView isDesktop={isDesktop}>
              <StyledContainerColumn isDesktop={isDesktop}>
                <StyledHeaderView isDesktop={isDesktop}>
                  <StyledH1 color='light' padding={2}>
                    Chloe Platform Log In
                  </StyledH1>
                  <Subtitle color='light'>
                    Log in with your email and password to access your Chloe
                    studies.
                  </Subtitle>
                </StyledHeaderView>
              </StyledContainerColumn>
              <StyledContainerColumn isDesktop={isDesktop}>
                <StyledFormView isDesktop={isDesktop}>
                  <Formik
                    innerRef={formik => (formikRef.current = formik)}
                    initialValues={{
                      email: '',
                      password: ''
                    }}
                    validationSchema={Yup.object().shape({
                      email: Yup.string()
                        .email()
                        .required('Email is required.'),
                      password: Yup.string().required('Password is required.')
                    })}
                    onSubmit={formValues => {
                      // format birth date
                      login(formValues);
                    }}
                  >
                    <Form>
                      <ContentView>
                        <FormInput
                          keyboardType='email-address'
                          autoCapitalize='none'
                          autoCorrect={false}
                          name='email'
                          // label='Email'
                          placeholder='Email address'
                        />
                        <FormInput
                          secureTextEntry
                          name='password'
                          returnKeyType='go'
                          submitOnReturn={true}
                          // label='Password'
                          placeholder='Password'
                        />
                        {loadingError && (
                          <>
                            <Spacer size='small' />
                            <StyledP>
                              {loadingError.statusCode === 401
                                ? 'Incorrect email or password. Please try again.'
                                : loadingError.message}
                            </StyledP>
                          </>
                        )}
                        <Spacer size='large' />
                        <FormButton inverse outlined>
                          Log In
                        </FormButton>
                        <Spacer />
                        <P center color='light'>
                          {' '}
                          <Link
                            onPress={() => {
                              setForgotPassword(true);
                            }}
                          >
                            Forgot Password?
                          </Link>
                        </P>
                        <Spacer size='large' />
                        <P center color='light'>
                          Don&apos;t have an account?{' '}
                          <Link onPress={onRequestSignUp}>Sign Up</Link>
                        </P>
                        <Spacer size={4} />
                      </ContentView>
                    </Form>
                  </Formik>
                </StyledFormView>
              </StyledContainerColumn>
            </StyledContainerView>
          </ScrollView>
        </StyledDialog>
      </StyledDimContainer>
      {forgotPassword && (
        <ForgotPasswordModal
          visible
          onRequestClose={() => setForgotPassword(false)}
        />
      )}
    </Modal>
  );
};

export default LogInModal;
